import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Avatar,
  Chip,
  Typography,
  IconButton,
  CircularProgress,
  Backdrop,
  Autocomplete,
  InputAdornment,
} from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import CommonPagination from "../../Common/CommonPagination/CommonPagination";
import EmptyData from "../../Common/EmptyData/EmptyData";
import CommonShimmer from "../../Common/CommonShimmer/CommonShimmer";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CloseIcon from "@mui/icons-material/Close";
import AnalyticsOutlinedIcon from "@mui/icons-material/AnalyticsOutlined";
import {
  postApiVenueBased,
  postSaveVenueDocument,
  apiCallGetLocaitonList,
  apiCallVenueStatusUpdate,
  apiCallVenueStatusDelete,
} from "../../../Core/Apicall";
import { UploadImages } from "../../../Core/UploadImages";
import { usePaginationState } from "../../../hooks/usePaginationState";
import { SearchIcon } from "lucide-react";
import CommonStatus from "../../Common/CommonStatus/CommonStatus";
import { MoreHoriz } from "@mui/icons-material";
import CommonMenu from "../../Common/CommonMenu/CommonMenu";
import DeletePopup from "../../Common/DeletePopup/DeletePopup";

interface Venue {
  _id: string;
  venueName: string;
  venueType?: string;
  phoneCountryCode?: string;
  phoneNumber?: string;
  cityOrCounty?: string;
  stateOrRegion?: string;
  country?: string;
  approvalStatus?: "NotUploaded" | "Pending" | "Rejected" | "Approved" | "Assigned";
  coverImage?: {
    thumbnailUrl?: string;
    url?: string;
  };
  isActive?: boolean;
}

interface ProfileData {
  userId?: string;
  role?: number;
}

interface ApiResponse {
  data?: Venue[];
  totalCount?: number;
}

function VenuesOwners() {
  const [data, setData] = useState<Venue[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [selectedVenue, setSelectedVenue] = useState<Venue | null>(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuVenue, setMenuVenue] = useState<Venue | null>(null);

  const [file, setFile] = useState<string | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [uploadLoading, setUploadLoading] = useState<boolean>(false);
  const [fileUploadLoading, setFileUploadLoading] = useState<boolean>(false);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const isProfileData = useSelector((state: any) => state?.global?.profileData) as ProfileData | null;

  const navigate = useNavigate();
  const totalPages = Math.ceil(totalItems / rowsPerPage);

  const [locations, setLocations] = useState<any[]>([]);
  const [countries, setCountries] = useState<string[]>([]);
  const [states, setStates] = useState<any[]>([]);
  const [cities, setCities] = useState<string[]>([]);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [searchParams] = useSearchParams();
  const {
    page,
    search,
    setSearch,
    debouncedSearch,
    handlePageChange,
    setPage,
    country,
    setCountry,
    state,
    setState,
    city,
    setCity
  } = usePaginationState();

  const fetchVenues = async (): Promise<void> => {
    setLoading(true);
    try {
      const response = (await postApiVenueBased({
        search: debouncedSearch,
        country: country,
        state: state,
        city: city,
        offset: page,
        limit: rowsPerPage,
        userId: isProfileData?.userId,
      })) as ApiResponse;

      if (Array.isArray(response?.data)) {
        setData(response.data);
        setTotalItems(response?.totalCount || response?.data?.length || 0);
      } else {
        setData([]);
      }
    } catch (err) {
      console.error("Error fetching venues:", err);
      toast.error("Failed to fetch venues");
    } finally {
      setLoading(false);
    }
  };

  const fetchLocations = async () => {
    try {
      const res = await apiCallGetLocaitonList();
      if (res?.data) {
        const urlCountry = searchParams.get("country");
        const urlState = searchParams.get("state");
        setLocations(res.data);
        setCountries(res.data.map((c: any) => c.country));
        if (urlCountry) {
          const selectedCountry = res.data.find((c: any) => c.country === urlCountry);
          setStates(selectedCountry?.states || []);

          if (urlState) {
            const selectedState = selectedCountry?.states.find((s: any) => s.state === urlState);
            setCities(selectedState?.cities || []);
          }
        }
      }
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    fetchLocations();
  }, []);

  useEffect(() => {
    fetchVenues();
  }, [page, rowsPerPage, debouncedSearch, country, state, city]);


  const menuItems = [
    {
      label: "Edit",
      onClick: () => {
        if (menuVenue) {
          navigate("/admin/VenueUpdate", { state: { venue: menuVenue } });
        }
      },
    },
    // {
    //   label: "Delete",
    //   onClick: () => setDeleteDialogOpen(true),
    // },
  ];


  // 🔹 Handle Upload Dialog
  const handleOpenUpload = (venue: Venue): void => {
    setSelectedVenue(venue);
    setUploadDialogOpen(true);
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files?.[0];

    if (!file) {
      toast.error("Please select an image file first!");
      return;
    }

    setFileUploadLoading(true);

    try {
      const result = await UploadImages(file, "profile", undefined);
      console.log("Upload result:", result);

      if (result && result.location) {
        setFile(result.location);
        setPreview(result.location);
        toast.success("File uploaded successfully!");
      } else {
        toast.error("Upload failed. Please try again.");
      }
    } catch (error: any) {
      toast.error(
        error?.message || "Something went wrong while uploading. Please try again later."
      );
    } finally {
      setFileUploadLoading(false);
    }
  };

  const handleUpload = async (): Promise<void> => {
    if (!file) {
      toast.error("Please select a file!");
      return;
    }

    if (!selectedVenue?._id) {
      toast.error("Venue ID not found!");
      return;
    }

    setUploadLoading(true);

    try {
      const params = {
        userId: isProfileData?.userId,
        venueId: selectedVenue._id,
        documentType: "Image",
        documentPath: file,
      };

      const response = await postSaveVenueDocument(params);
      console.log("Upload response:", response);

      toast.success("Document uploaded successfully!");
      setUploadDialogOpen(false);
      setSelectedVenue(null);
      setFile(null);
      setPreview(null);
      fetchVenues();
    } catch (err) {
      console.error("Upload error:", err);
      toast.error("Upload failed due to network error");
    } finally {
      setUploadLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!menuVenue) return;
    try {
      await apiCallVenueStatusDelete({
        venueId: menuVenue._id,
        isDelete: true,
      });
      setData((prev) => prev.filter((item) => item._id !== menuVenue._id));
      toast.success("Venue deleted successfully!");
    } catch (err) {
      toast.error("Failed to delete venue");
    } finally {
      setDeleteDialogOpen(false);
      setMenuVenue(null);
    }
  };

  const handleCloseDialog = (): void => {
    setUploadDialogOpen(false);
    setFile(null);
    setPreview(null);
    setSelectedVenue(null);
    setFileUploadLoading(false);
    setUploadLoading(false);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };


  // 🔹 Get status chip color
  const getStatusColor = (status?: string): "success" | "error" | "warning" | "default" => {
    switch (status) {
      case "Approved":
        return "success";
      case "Rejected":
        return "error";
      case "Pending":
        return "warning";
      default:
        return "default";
    }
  };


  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    venue: Venue
  ) => {
    setAnchorEl(event.currentTarget);
    setMenuVenue(venue);
  };

  const handleStatusUpdate = async (venue: Venue) => {
    try {
      await apiCallVenueStatusUpdate({
        venueId: venue._id,
        isActive: !venue.isActive,
      });
      setData((prev) =>
        prev.map((item) =>
          item._id === venue._id ? { ...item, isActive: !venue.isActive } : item
        )
      );
      toast.success(`Venue ${venue.isActive ? 'deactivated' : 'activated'} successfully!`);
      setStatusDialogOpen(false);
    } catch (err) {
      toast.error("Failed to update status");
    }
  };


  const onClickVenue = (item: any) => {
    navigate("/admin/VenueDetails", {
      state: {
        venue: item,
        backPage: page,
      },
    });
  };


  return (
    <Box sx={{ paddingBottom: "20px", overflow: "auto" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          padding: "12px 16px",
          background: "#FFFF",
          borderRadius: "12px",
          marginBottom: "16px",
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
          <TextField
            size="small"
            placeholder="Search venue..."
            value={search}
            onChange={(e) => { setPage(1); setSearch(e.target.value) }}
            sx={{
              width: 280,
              background: "#fff",
              borderRadius: "8px",
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          <Autocomplete
            size="small"
            options={countries}
            ListboxProps={{
              sx: { fontSize: "14.5px", fontWeight: 'bold' }
            }}
            value={country}
            onChange={(e, value) => {
              setCountry(value);
              setState(null);
              setCity(null);
              setPage(1);
              const selectedCountry = locations.find(
                (c: any) => c.country === value
              );
              setStates(selectedCountry?.states || []);
              setCities([]);
            }}
            sx={{ width: 220, background: "#fff" }}
            renderInput={(params) => (
              <TextField {...params} label="Country" />
            )}
          />

          <Autocomplete
            size="small"
            options={states.map((s: any) => s.state)}
            value={state}
            ListboxProps={{
              sx: { fontSize: "14.5px", fontWeight: 'bold' }
            }}
            onChange={(e, value) => {
              setState(value);
              setCity(null);
              setPage(1);
              const selectedState = states.find(
                (s: any) => s.state === value
              );
              setCities(selectedState?.cities || []);
            }}
            sx={{ width: 220, background: "#fff" }}
            renderInput={(params) => (
              <TextField {...params} label="State" />
            )}
          />

          <Autocomplete
            size="small"
            options={cities}
            value={city}
            ListboxProps={{
              sx: { fontSize: "14.5px", fontWeight: 'bold' }
            }}
            onChange={(e, value) => {
              setCity(value);
              setPage(1);
            }}
            sx={{ width: 220, background: "#fff" }}
            renderInput={(params) => (
              <TextField {...params} label="City" />
            )}
          />
        </Box>

        <Button
          onClick={() => navigate("/admin/Add-Venue")}
          variant="contained"
          sx={{ backgroundColor: "black", '&:hover': { backgroundColor: "#333" } }}
        >
          Create New
        </Button>
      </Box>

      {/* 🧾 Venue Table */}
      <Paper
        className="bg-white mt-3 border-20 scroll-bar"
        sx={{ borderRadius: 3, overflow: "hidden" }}
      >
        <TableContainer className="scroll-bar">
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Venue</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Address</TableCell>
                <TableCell>Approval Status</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>

            {!loading && (
              <TableBody>
                {data.map((item: Venue, index: any) => (
                  <TableRow hover key={item._id}>
                    {/* 🏢 Venue */}
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Avatar
                          src={
                            item?.coverImage?.thumbnailUrl ||
                            item?.coverImage?.url ||
                            "https://via.placeholder.com/50"
                          }
                          alt={item?.venueName}
                          sx={{ width: 45, height: 45 }}
                        />
                        <Box
                          sx={{ cursor: "pointer", fontWeight: 500 }}
                          onClick={() =>
                            onClickVenue(item)
                          }
                        >
                          {item?.venueName ? item.venueName.slice(0, 10) + "..." : "-"}
                        </Box>
                      </Box>
                    </TableCell>

                    {/* Type */}
                    <TableCell sx={{cursor: "pointer",}} onClick={() =>
                      onClickVenue(item)
                    }>{item?.venueType || "-"}</TableCell>

                    {/* Phone */}
                    <TableCell sx={{cursor: "pointer",}} onClick={() =>
                      onClickVenue(item)
                    }>
                      {item?.phoneCountryCode} {item?.phoneNumber || "-"}
                    </TableCell>

                    {/* City / State / Country */}
                    <TableCell sx={{cursor: "pointer",}} onClick={() =>
                      onClickVenue(item)
                    }>
                      {item?.cityOrCounty || "-"}
                      {item?.stateOrRegion && `, ${item.stateOrRegion}`}
                      {item?.country && `, ${item.country}`}
                    </TableCell>

                    {/* Approval Status */}
                    <TableCell>


                      {item?.approvalStatus === "Assigned" && (
                        <Button
                          variant="contained"
                          style={{
                            backgroundColor: "#FF9800", // Orange color for assigned
                            minWidth: "auto",
                            padding: "4px 12px",
                            fontSize: "0.7rem",
                            whiteSpace: "nowrap"
                          }}
                          size="small"
                          onClick={() => handleOpenUpload(item)}
                        >
                          Upload Document
                        </Button>
                      )}


                      {/* Upload Document Button - Side by Side */}
                      {item?.approvalStatus === "NotUploaded" && (
                        <Button
                          variant="contained"
                          style={{
                            backgroundColor: "black",
                            minWidth: "auto",
                            padding: "4px 12px",
                            fontSize: "0.7rem",
                            whiteSpace: "nowrap"
                          }}
                          size="small"
                          onClick={() => handleOpenUpload(item)}
                        >
                          Upload Document
                        </Button>
                      )}

                      {item?.approvalStatus === "Pending" && (
                        <Chip
                          label="Pending"
                          color="warning"
                          variant="outlined"
                          size="small"
                        />
                      )}

                      {item?.approvalStatus === "Rejected" && (
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          style={{
                            minWidth: "auto",
                            padding: "4px 12px",
                            fontSize: "0.7rem",
                            whiteSpace: "nowrap"
                          }}
                          onClick={() => handleOpenUpload(item)}
                        >
                          Re-upload Document
                        </Button>
                      )}

                      {item?.approvalStatus === "Approved" && (
                        <Chip
                          label="Approved"
                          color="success"
                          variant="outlined"
                          size="small"
                        />
                      )}
                    </TableCell>

                    {/* Status Column with Switch */}
                    <TableCell>
                      <Button
                        className={
                          item?.isActive ? "btn-success" : "btn-danger"
                        }
                        onClick={() => setStatusDialogOpen(index)}
                      >
                        {item?.isActive ? "Active" : "Inactive"}
                      </Button>
                      <CommonStatus
                        open={statusDialogOpen === index}
                        title={item?.venueName}
                        message={`Are you sure you want to ${item?.isActive ? "deactivate" : "activate"
                          } this venue?`}
                        confirmText={item?.isActive ? "Deactivate" : "Activate"}
                        confirmColor={item?.isActive ? "warning" : "success"}
                        onConfirm={() => handleStatusUpdate(item)}
                        onClose={() => setStatusDialogOpen(false)}
                      />
                    </TableCell>

                    {/* 🔥 Action Column - Fixed UI */}
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                        {/* Analytics Icon */}
                        <IconButton
                          size="small"
                          onClick={() =>
                            navigate("/admin/Analytics", {
                              state: { venueId: item._id },
                            })
                          }
                        >
                          <AnalyticsOutlinedIcon fontSize="small" />
                        </IconButton>

                        {/* More Options Menu */}
                        <IconButton onClick={(e) => handleMenuOpen(e, item)}>
                          <MoreHoriz />
                        </IconButton>

                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            )}
          </Table>

          {/* ⏳ Loading */}
          {loading && (
            <Box sx={{ p: 2 }}>
              <CommonShimmer />
            </Box>
          )}
        </TableContainer>

        {/* 📭 Empty State */}
        {!loading && <EmptyData data={data} />}

        {/* 🔢 Pagination */}
        <CommonPagination
          page={page}
          rowsPerPage={rowsPerPage}
          totalItems={totalItems}
          totalPages={totalPages}
          onPageChange={(value: number) => setPage(value)}
          onRowsPerPageChange={(value: number) => {
            setRowsPerPage(value);
            setPage(1);
          }}
        />
      </Paper>

      <DeletePopup
        open={deleteDialogOpen}
        title="Confirm Delete"
        message="Are you sure you want to delete this venue?"
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDelete}
        onClose={() => setDeleteDialogOpen(false)}
      />


      {/* 📤 Upload Dialog */}
      <Dialog
        open={uploadDialogOpen}
        onClose={!uploadLoading && !fileUploadLoading ? handleCloseDialog : undefined}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{
          fontWeight: 600,
          fontSize: '1.1rem',
          borderBottom: '1px solid #e0e0e0',
          pb: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <span>Upload Document for {selectedVenue?.venueName}</span>
          {(uploadLoading || fileUploadLoading) && (
            <CircularProgress size={24} />
          )}
        </DialogTitle>

        <DialogContent sx={{ pt: 3 }}>
          {fileUploadLoading ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                minHeight: "200px",
                gap: 2,
              }}
            >
              <CircularProgress size={60} sx={{ color: "black" }} />
              <Typography variant="body1" sx={{ fontWeight: 500, color: "#333" }}>
                Uploading file...
              </Typography>
              <Typography variant="caption" sx={{ color: "#666" }}>
                Please wait while your file is being uploaded
              </Typography>
            </Box>
          ) : (
            <>
              {!preview ? (
                <Box
                  component="label"
                  htmlFor="fileInput"
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "100%",
                    minHeight: "180px",
                    border: "2px dashed #D1D5DB",
                    borderRadius: 3,
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                    backgroundColor: "#FAFAFA",
                    "&:hover": {
                      borderColor: "#000000",
                      backgroundColor: "#F5F5F5",
                    },
                    gap: 1,
                  }}
                >
                  <CloudUploadIcon sx={{ fontSize: 50, color: "#9CA3AF" }} />
                  <Typography variant="body1" sx={{ color: "#6B7280", fontWeight: 500 }}>
                    Click to upload
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#9CA3AF" }}>
                    Supported: JPG, PNG, PDF, DOCX
                  </Typography>
                </Box>
              ) : (
                <Box
                  sx={{
                    position: "relative",
                    width: "100%",
                    borderRadius: 3,
                    overflow: "hidden",
                    border: "2px solid #e0e0e0",
                    backgroundColor: "#FAFAFA",
                  }}
                >
                  <img
                    src={preview}
                    alt="Preview"
                    style={{
                      width: "100%",
                      maxHeight: "300px",
                      objectFit: "contain",
                      display: "block",
                    }}
                  />

                  <IconButton
                    onClick={() => {
                      setFile(null);
                      setPreview(null);
                      const fileInput = document.getElementById('fileInput') as HTMLInputElement;
                      if (fileInput) fileInput.value = '';
                    }}
                    sx={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      backgroundColor: "rgba(0,0,0,0.7)",
                      color: "white",
                      "&:hover": {
                        backgroundColor: "rgba(0,0,0,0.9)",
                      },
                      width: 36,
                      height: 36,
                    }}
                  >
                    <CloseIcon />
                  </IconButton>

                  <Box
                    component="label"
                    htmlFor="fileInput"
                    sx={{
                      position: "absolute",
                      bottom: 16,
                      right: 16,
                      backgroundColor: "rgba(0,0,0,0.7)",
                      color: "white",
                      padding: "6px 16px",
                      borderRadius: 2,
                      cursor: "pointer",
                      fontSize: "0.8rem",
                      fontWeight: 500,
                      "&:hover": {
                        backgroundColor: "rgba(0,0,0,0.9)",
                      },
                    }}
                  >
                    Change File
                  </Box>

                  <Box
                    sx={{
                      position: "absolute",
                      bottom: 16,
                      left: 16,
                      backgroundColor: "rgba(0,0,0,0.6)",
                      color: "white",
                      padding: "4px 12px",
                      borderRadius: 2,
                      fontSize: "0.75rem",
                      maxWidth: "50%",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {file?.split('/').pop() || 'File uploaded'}
                  </Box>
                </Box>
              )}

              <input
                id="fileInput"
                type="file"
                accept="image/*,.pdf,.docx"
                onChange={handleFileChange}
                style={{ display: "none" }}
                disabled={fileUploadLoading}
              />
            </>
          )}
        </DialogContent>

        <DialogActions sx={{
          borderTop: '1px solid #e0e0e0',
          p: 2,
          gap: 1
        }}>
          <Button
            onClick={handleCloseDialog}
            disabled={uploadLoading || fileUploadLoading}
            sx={{
              color: "#666",
              fontWeight: 500,
              '&:hover': {
                backgroundColor: "#f5f5f5"
              },
              '&:disabled': {
                color: "#ccc",
              }
            }}
          >
            Cancel
          </Button>

          <Button
            onClick={handleUpload}
            variant="contained"
            disabled={!file || uploadLoading || fileUploadLoading}
            sx={{
              backgroundColor: "black",
              fontWeight: 500,
              px: 4,
              minWidth: "150px",
              '&:hover': {
                backgroundColor: "#333",
              },
              '&:disabled': {
                backgroundColor: "#999",
                color: "#ccc",
              }
            }}
          >
            {uploadLoading ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={20} sx={{ color: "white" }} />
                <span>Uploading...</span>
              </Box>
            ) : (
              "Upload Document"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <CommonMenu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        items={menuItems}
      />


      {/* Backdrop for Upload */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={uploadLoading}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CircularProgress color="inherit" size={60} />
          <Typography variant="h6" sx={{ color: 'white' }}>
            Uploading document...
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
            Please wait while we process your document
          </Typography>
        </Box>
      </Backdrop>
    </Box>
  );
}

export default VenuesOwners;